import { Prisma } from '../../generated/prisma/client.js';

export enum LayerType {
  ASSET = 'ASSET',
  OPERATIONAL = 'OPERATIONAL',
}

export enum SystemLayerKey {
  SERVICE_AREA = 'SERVICE_AREA',
  PREMISE = 'PREMISE',
  FIELD_NOTE = 'FIELD_NOTE',
  TICKET = 'TICKET',
  ASSET = 'ASSET',
  CREW_LOCATION = 'CREW_LOCATION',
}

export enum FieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  SELECT = 'select',
  TEXTAREA = 'textarea',
}

// =========================================================================
// SCHEMA CONFIG
// =========================================================================

export type FieldOptions = {
  label: string;
  value: string | number;
}[];

export type FieldOptionsConfig = {
  options: FieldOptions;
  defaultValue?: string | number;
};

export interface LayerSchemaConfigProperty {
  column: string; // actual DB column name
  alias: string; // camelCase key used in GeoJSON properties

  // Form properties (asset redlining)
  fieldLabel?: string; // UI display label
  fieldName?: string; // form field name for create/edit
  fieldType?: FieldType;
  fieldOptions?: FieldOptionsConfig; // only for FieldType.SELECT
  isEditable?: boolean; // show in create / edit form
  isRequired?: boolean; // form validation

  // UI Display properties
  isSearchable: boolean; // include in search queries
  isLabel: boolean; // use as primary label in UI (e.g. feature cards)

  // Set visibility based on context
  showInForm?: boolean;
  showInPopup?: boolean; // Popup click in map
  showInCard?: boolean;
  showInDetail?: boolean; // Detail card
}

export interface LayerSchemaConfig {
  properties: LayerSchemaConfigProperty[];
}

// =========================================================================
// STYLES
// =========================================================================

export type ShapeType = 'circle' | 'square' | 'line' | 'polygon';

export interface LabelConfig {
  field: string; // schemaProperty alias to read from feature
  maxLength?: number;
  font?: string;
  color?: string;
  offsetY?: number;
  background?: string;
}

export interface LayerStyles {
  color?: string; // hex
  opacity?: number; // 0–1
  pointType?: 'icon' | 'triangle' | 'star' | 'square';
  pointSize?: number; // points only
  iconUrl?: string; // points only, overrides shape if provided
  iconRotation?: number; // points only, degrees to rotate icon
  lineWidth?: number;
  lineDash?: number[]; // lines only, e.g. [10, 10] for dashed
  strokeColor?: string; // hex, for stroke / polygon border
  fillColor?: string;
  label: LabelConfig;
  showLabel: boolean;
}

// =========================================================================
// SOURCE CONFIG
// =========================================================================

// sourceFilter narrows rows within a shared table e.g. assets
export type SourceFilter = {
  assetType?: string;
  joinTable?: string;
  [key: string]: unknown; // extensible for future filter types
} | null;

export type LayerDefinitionPayload = Prisma.LayerDefinitionGetPayload<{}>;

// Extended LayerDefinition with additional types for frontend use
export type LayerDefinition = Omit<
  LayerDefinitionPayload,
  | 'organizationId'
  | 'key'
  | 'sourceFilter'
  | 'styles'
  | 'layerType'
  | 'schemaConfig'
  | 'zIndex'
  | 'minZoom'
  | 'maxZoom'
  | 'opacity'
  | 'isVisible'
> & {
  // Omit these for frontend types
  organizationId?: string;
  // Updated types for frontend for consistency
  key: SystemLayerKey | string; // key is unique to each asset and is configured per tenant
  sourceFilter: SourceFilter;
  styles: LayerStyles;
  layerType: LayerType;
  schemaConfig: LayerSchemaConfig;
  // Optional
  zIndex?: number;
  minZoom?: number;
  maxZoom?: number;
  opacity?: number;
  isVisible?: boolean;
};

// =========================================================================
// GEOJSON FEATURE PROPERTIES
// Typed wrapper so feature click handlers know what they're working with
// =========================================================================

export interface BaseFeatureProperties {
  id: string;
  sourceType: string; // mirrors sourceTable for click resolution
}

export interface AssetFeatureProperties extends BaseFeatureProperties {
  name: string;
  structureNo?: string;
  owner?: string;
  assetStatus?: string;
  assetTypeSlug: string;
  inspectionDate?: string;
  acquireCost?: number;
}

export interface TicketFeatureProperties extends BaseFeatureProperties {
  ticketNumber: string;
  status: string;
  priority: string;
  description: string;
  address1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  startDate?: string;
  endDate?: string;
  createdAt: string;
}

export interface FieldNoteFeatureProperties extends BaseFeatureProperties {
  content: string;
  isPublic: boolean;
  createdAt: string;
  createdBy: string;
}

export interface ServiceAreaFeatureProperties extends BaseFeatureProperties {
  name: string;
  description?: string;
}

export interface PremiseFeatureProperties extends BaseFeatureProperties {
  streetName: string;
  city: string;
  state: string;
  zipcode: string;
  type: 'RESIDENTIAL' | 'COMMERCIAL';
  sourceId?: string;
}

// Union type for click handlers
export type FeatureProperties =
  | AssetFeatureProperties
  | TicketFeatureProperties
  | FieldNoteFeatureProperties
  | ServiceAreaFeatureProperties
  | PremiseFeatureProperties;

// =========================================================================
// LAYER QUERY PARAMS
// Passed to /api/layers/:sourceTable/features
// =========================================================================

export interface LayerQueryParams {
  tenantId?: string;
  bbox?: [number, number, number, number];
  assetType?: string;
  limit?: number;
  offset?: number;
}
